/**
 * fleetComms — fleet-web's view of the canonical robot-comms contract, built on the
 * generated channel table (`./generated/channels`, emitted at build time by
 * robot-comms/tools/gen-contract.ts — the SAME generator operator-mqtt/web runs).
 *
 * Why this is not operator-mqtt/web/src/contract/robotComms.ts: that binding pins ONE
 * robot and one static prefix, because an operator drives one robot at a time. This app
 * is the opposite shape — it watches every site and every robot the token grants, so the
 * site AND the robot are topic variables here, not constructor arguments. Same contract,
 * same generated table, different arity. Collapsing the two means making `robotId` and
 * the root optional everywhere, which is how a binding stops being checkable.
 *
 * The cloud root is `sites/{siteId}/`, the single deployment difference between the two
 * brokers — see "Deployment roots" in asyncapi.yaml. On-prem uses addresses as written;
 * cloud mounts the identical tree under the site. That the two tiers differ by the
 * prefix and NOTHING else is asserted channel-by-channel in
 * robot-comms/tests/test_contract.py, which is what lets this file hold one constant
 * instead of a translation table.
 *
 * Nothing outside this file may write an MQTT topic. That is the whole point: a
 * hand-written tail is a second reading of the spec, and this app has already shipped
 * one bug of exactly that kind (the roster consumer read `robotId` where the schema
 * says `robot_id`).
 */
import { CHANNELS, type Channel, type ChannelName } from "./generated/channels";

export type { ChannelName };

/** The cloud deployment root. Every address in the contract hangs under this. */
const ROOT = "sites/{siteId}/";

const BY_NAME = new Map<string, Channel>(CHANNELS.map((c) => [c.name, c]));

// `sites/{siteId}/robots/{robotId}/telemetry/battery`
//   -> ^sites/(?<siteId>[^/]+)/robots/(?<robotId>[^/]+)/telemetry/battery$
function addressToRegex(address: string): RegExp {
  const escaped = address
    .split(/(\{[^}]+\})/)
    .map((p) =>
      p.startsWith("{") && p.endsWith("}")
        ? `(?<${p.slice(1, -1)}>[^/]+)`
        : p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    )
    .join("");
  return new RegExp(`^${escaped}$`);
}

// Compiled once at module load, not per message: this runs on every inbound packet.
const MATCHERS = CHANNELS.map((c) => ({
  channel: c,
  re: addressToRegex(ROOT + c.address),
}));

function lookup(name: ChannelName): Channel {
  const c = BY_NAME.get(name);
  // Naming a channel the contract does not define is a programming error, and a silent
  // fallback would publish to a topic nobody subscribes — the failure mode whose symptom
  // is nothing happening. Fail loudly instead.
  if (!c) throw new Error(`unknown channel ${name} (see robot-comms asyncapi.yaml)`);
  return c;
}

/** Every `{name}` in an address, in order. `["robotId"]`, `["entryId"]`, or nothing. */
function addressParams(address: string): string[] {
  return [...address.matchAll(/\{([^}]+)\}/g)].map((m) => m[1]);
}

/**
 * Fill an address's parameters, refusing every way of getting it wrong.
 *
 * Mirrors `RobotComms.topic` in spec.py, and for the same reason: all three failures
 * below produce a topic that LOOKS fine and silently goes nowhere. EMQX answers a denied
 * publish with success (`deny_action = ignore`), so a malformed topic is
 * indistinguishable from a robot with nothing to say.
 *
 *   - a missing parameter leaves a literal `{entryId}` in the topic;
 *   - an extra one is a typo, which leaves the real one unfilled;
 *   - a value containing `/`, `+` or `#` adds a topic level or turns a publish into a
 *     wildcard. (A Keenon serial is `94:BA:06:CA:99:50` — `:` is fine in MQTT, `/` is not.)
 */
function fill(name: ChannelName, address: string, supplied: Record<string, string>): string {
  const wanted = addressParams(address);
  for (const key of Object.keys(supplied)) {
    if (!wanted.includes(key)) {
      throw new Error(`channel ${name} has no address parameter ${key}; it takes [${wanted}]`);
    }
  }
  let out = address;
  for (const key of wanted) {
    const value = supplied[key];
    if (value === undefined) {
      throw new Error(`channel ${name} needs address parameter ${key}`);
    }
    if (!value || /[/+#]/.test(value)) {
      throw new Error(`channel ${name}: ${key}="${value}" cannot be used in a topic`);
    }
    out = out.replace(`{${key}}`, value);
  }
  return out;
}

/** A per-robot channel's concrete topic. Throws if the channel is site-level. */
export function robotTopic(siteId: string, robotId: string, name: ChannelName): string {
  const c = lookup(name);
  if (!c.perRobot) throw new Error(`${name} is site-level; use fleetTopic`);
  return `sites/${siteId}/${fill(name, c.address, { robotId })}`;
}

/**
 * A site-level channel's concrete topic (`fleet/…`, `cmd/list`, `cmd/debug_query`).
 *
 * `params` fills any address parameter that is not `{robotId}` — today that means
 * `fleetDebugCache`'s `entryId`, which is one topic per cached answer. Omitting it
 * throws rather than shipping a literal `fleet/debug_cache/{entryId}` to the broker.
 */
export function fleetTopic(
  siteId: string,
  name: ChannelName,
  params: Record<string, string> = {},
): string {
  const c = lookup(name);
  if (c.perRobot) throw new Error(`${name} is per-robot; use robotTopic`);
  return `sites/${siteId}/${fill(name, c.address, params)}`;
}

export const qos = (name: ChannelName): number => lookup(name).qos;
export const retain = (name: ChannelName): boolean => lookup(name).retain;

/**
 * Everything this app can see, as a subscribe filter. Note it is `sites/+/#` rather than
 * the per-robot shape: the site-level family (`fleet/robots`, the debug channels) sits
 * beside `robots/`, not under it, and a filter requiring a robot segment drops exactly
 * the message the roster is built from.
 *
 * The BROKER decides what actually arrives — this is the ceiling, not the grant.
 */
export const ALL_TOPICS = "sites/+/#";

export interface ParsedTopic {
  siteId: string;
  /** null for the site-level family — the channel is about the fleet, not one robot. */
  robotId: string | null;
  name: ChannelName;
  /** Every address parameter the topic carried, e.g. `{entryId}` on a cache entry. */
  params: Record<string, string>;
}

/**
 * Map a concrete inbound topic back to its CHANNEL NAME. Callers must switch on the
 * name, never on an address tail: the adapter crash-looped once because `"cmd/poll"`
 * was passed where `cmdPoll` belonged, and a tail is a string the contract can rename
 * out from under you without anything failing to compile.
 */
export function parseTopic(topic: string): ParsedTopic | null {
  for (const { channel, re } of MATCHERS) {
    const m = re.exec(topic);
    if (!m) continue;
    const siteId = m.groups?.siteId;
    if (!siteId) return null;
    const params: Record<string, string> = { ...m.groups };
    delete params.siteId;      // carried separately; it is the root, not the address
    return {
      siteId,
      robotId: channel.perRobot ? (m.groups?.robotId ?? null) : null,
      name: channel.name as ChannelName,
      params,
    };
  }
  return null;
}
